import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Loading from '../components/Loading';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchCode, setSearchCode] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searching, setSearching] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      fetchOrders();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await orderService.getMyOrders();
      setOrders(response.data.data || []);
      setError('');
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('فشل في تحميل الطلبات');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchCode.trim() || !searchPhone.trim()) {
      setError('الرجاء إدخال رقم الطلب ورقم الهاتف');
      return;
    }

    try {
      setSearching(true);
      setError('');
      const response = await orderService.searchByCodeAndPhone(searchCode.trim(), searchPhone.trim());
      
      if (response.data.data) {
        setOrders([response.data.data]);
      } else {
        setOrders([]);
        setError('لم يتم العثور على طلب بهذه المعلومات');
      }
    } catch (error) {
      console.error('Error searching order:', error);
      setError(error.response?.data?.message || 'لم يتم العثور على طلب بهذه المعلومات');
      setOrders([]);
    } finally {
      setSearching(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'قيد الانتظار';
      case 'processing':
        return 'قيد المعالجة';
      case 'shipped':
        return 'تم الشحن';
      case 'delivered':
        return 'تم التوصيل';
      case 'cancelled':
        return 'ملغي';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-IQ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const handleViewDetails = async (order) => {
    try {
      // Fetch full order details including items with SKU
      const response = await orderService.getById(order.id);
      setSelectedOrder(response.data.data);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching order details:', error);
      setError('فشل في تحميل تفاصيل الطلب');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
  };

  if (loading) return <Loading />;

  return (
    <div className="container mx-auto px-4 py-8" dir="rtl">
      <h1 className="text-3xl font-bold mb-8 text-right">{user ? 'طلباتي' : 'البحث عن طلب'}</h1>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Guest Search Form */}
      {!user && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">تتبع طلبك</h2>
          <p className="text-gray-600 mb-6">أدخل رقم الطلب ورقم الهاتف المستخدم عند الطلب</p>
          
          <form onSubmit={handleSearch} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                رقم الطلب
              </label>
              <input
                type="text"
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value)}
                placeholder="مثال: ORD-ABC12345"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                رقم الهاتف
              </label>
              <input
                type="tel"
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                placeholder="07xxxxxxxxx"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={searching}
              className="w-full bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
            >
              {searching ? 'جاري البحث...' : 'بحث عن الطلب'}
            </button>
          </form>
        </div>
      )}

      {orders.length === 0 && !searching ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <svg
            className="w-24 h-24 mx-auto mb-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
            />
          </svg>
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">
            {user ? 'لا توجد طلبات بعد' : 'لم يتم العثور على طلبات'}
          </h2>
          <p className="text-gray-500 mb-6">
            {user ? 'ابدأ التسوق الآن وقدم طلبك الأول' : 'استخدم نموذج البحث أعلاه للعثور على طلبك'}
          </p>
          {user && (
            <button
              onClick={() => navigate('/products')}
              className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-700"
            >
              تصفح المنتجات
            </button>
          )}
        </div>
      ) : orders.length > 0 ? (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      رقم الطلب
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      التاريخ
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الحالة
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      المجموع
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الإجراءات
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{order.order_code}</div>
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(order.created_at)}</div>
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 lg:px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                          {getStatusText(order.status)}
                        </span>
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-primary">{order.total_amount?.toLocaleString()} د.ع</div>
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewDetails(order)}
                          className="bg-primary text-white px-3 lg:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                          عرض التفاصيل
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg shadow-md p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">رقم الطلب</p>
                    <p className="font-bold text-sm">{order.order_code}</p>
                  </div>
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                    {getStatusText(order.status)}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">التاريخ</p>
                    <p className="text-sm font-medium">{formatDate(order.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">المجموع</p>
                    <p className="text-sm font-semibold text-primary">{order.total_amount?.toLocaleString()} د.ع</p>
                  </div>
                </div>

                <button
                  onClick={() => handleViewDetails(order)}
                  className="w-full bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  عرض التفاصيل
                </button>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {/* Order Details Modal */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-0 sm:p-4" onClick={closeModal}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-4xl h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} dir="rtl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 flex justify-between items-center z-10 rounded-t-2xl sm:rounded-t-lg">
              <h2 className="text-lg sm:text-2xl font-bold">تفاصيل الطلب</h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Customer Details Section */}
            <div className="bg-gray-50 px-4 py-4 border-b border-gray-200">
              <h3 className="text-base font-bold mb-3 text-gray-800">معلومات العميل</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">الاسم</p>
                    <p className="font-medium text-sm text-gray-900">
                      {(() => {
                        try {
                          const addressData = JSON.parse(selectedOrder.shipping_address);
                          return addressData.name || 'غير متوفر';
                        } catch {
                          if (selectedOrder.first_name && selectedOrder.last_name) {
                            return `${selectedOrder.first_name} ${selectedOrder.last_name}`;
                          }
                          return 'غير متوفر';
                        }
                      })()}
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">المدينة</p>
                    <p className="font-medium text-sm text-gray-900">
                      {(() => {
                        try {
                          const addressData = JSON.parse(selectedOrder.shipping_address);
                          return addressData.city || 'غير متوفر';
                        } catch {
                          return 'غير متوفر';
                        }
                      })()}
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">رقم الطلب</p>
                    <p className="font-bold text-sm text-primary">{selectedOrder.order_code}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">التاريخ</p>
                    <p className="font-medium text-sm text-gray-900">{formatDate(selectedOrder.created_at)}</p>
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">الحالة</p>
                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedOrder.status)}`}>
                    {getStatusText(selectedOrder.status)}
                  </span>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">العنوان</p>
                  <p className="font-medium text-sm text-gray-900 leading-relaxed">
                    {(() => {
                      try {
                        const addressData = JSON.parse(selectedOrder.shipping_address);
                        return addressData.address || selectedOrder.shipping_address;
                      } catch {
                        return selectedOrder.shipping_address;
                      }
                    })()}
                  </p>
                </div>
                {(() => {
                  try {
                    const addressData = JSON.parse(selectedOrder.shipping_address);
                    if (addressData.notes) {
                      return (
                        <div className="bg-white p-3 rounded-lg">
                          <p className="text-xs text-gray-500 mb-1">ملاحظات</p>
                          <p className="font-medium text-sm text-gray-900 leading-relaxed">{addressData.notes}</p>
                        </div>
                      );
                    }
                  } catch {
                    return null;
                  }
                })()}
              </div>
            </div>

            {/* Products Section - Mobile Optimized */}
            <div className="px-4 py-4">
              <h3 className="font-semibold text-base mb-3">المنتجات</h3>
              
              {/* Mobile: Card Layout */}
              <div className="md:hidden space-y-3">
                {selectedOrder.items && selectedOrder.items.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex gap-3 mb-2">
                      <div className="w-16 h-16 flex-shrink-0 bg-white rounded overflow-hidden border border-gray-200">
                        {item.image_url ? (
                          <img
                            src={item.image_url.startsWith('http') ? item.image_url : `https://api.iraq-parts.com${item.image_url}`}
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xl">
                            📦
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm text-gray-900 mb-1 line-clamp-2">{item.product_name}</h4>
                        <p className="text-xs text-gray-500">SKU: {item.part_number || 'غير متوفر'}</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                      <div className="flex gap-4">
                        <div>
                          <p className="text-xs text-gray-500">الكمية</p>
                          <p className="text-sm font-medium">{item.quantity}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">السعر</p>
                          <p className="text-sm font-semibold">{item.price?.toLocaleString()} د.ع</p>
                        </div>
                      </div>
                      <div className="text-left">
                        <p className="text-xs text-gray-500">الإجمالي</p>
                        <p className="text-sm font-bold text-primary">{(item.price * item.quantity)?.toLocaleString()} د.ع</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: Table Layout */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        الصورة
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        اسم المنتج
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        SKU
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        الكمية
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        السعر
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        الإجمالي
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedOrder.items && selectedOrder.items.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="w-16 h-16 flex-shrink-0 bg-white rounded overflow-hidden border border-gray-200">
                            {item.image_url ? (
                              <img
                                src={item.image_url.startsWith('http') ? item.image_url : `https://api.iraq-parts.com${item.image_url}`}
                                alt={item.product_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400 text-2xl">
                                📦
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">{item.product_name}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.part_number || 'غير متوفر'}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.quantity}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">{item.price?.toLocaleString()} د.ع</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-primary">{(item.price * item.quantity)?.toLocaleString()} د.ع</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Order Summary */}
              <div className="mt-4 space-y-2">
                <div className="flex justify-between items-center py-2 border-t border-gray-200">
                  <span className="text-sm text-gray-700">المجموع الفرعي:</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {selectedOrder.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0).toLocaleString()} د.ع
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-t border-gray-200">
                  <span className="text-sm text-gray-700">رسوم التوصيل:</span>
                  <span className="text-sm font-semibold text-gray-900">5,000 د.ع</span>
                </div>
                <div className="flex justify-between items-center py-3 border-t-2 border-gray-300 bg-gray-50 px-3 rounded-lg">
                  <span className="text-base font-bold text-gray-900">المجموع الكلي:</span>
                  <span className="text-lg font-bold text-primary">{selectedOrder.total_amount?.toLocaleString()} د.ع</span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white px-4 py-3 border-t border-gray-200 shadow-lg">
              <button
                onClick={closeModal}
                className="w-full bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 transition-colors text-sm font-semibold"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
